"use client";
import { useEffect, useState } from "react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { AspectRatio } from "@radix-ui/themes";
import { MegaphoneIcon } from "@heroicons/react/24/solid";
import { EventData } from "@types";

const BANNER_RATIO = 3.28 / 1;
const TARGET_WIDTH = 2000;
const TARGET_HEIGHT = Math.round(TARGET_WIDTH / BANNER_RATIO); // 610

const isUploadcare = (url: string) => url.includes("ucarecd.net");

function buildUploadcareSrc(url: string) {
  const base = url.split("-/")[0].replace(/\/+$/, "");
  const ops = [
    `scale_crop/${TARGET_WIDTH}x${TARGET_HEIGHT}/smart`,
    "format/auto",
    "quality/smart",
  ];
  return `${base}/${ops.map((op) => `-/${op}`).join("/")}/`;
}

export default function ImageBanner({
  images,
  eventName,
}: {
  images: EventData["images"];
  eventName: string;
}) {
  const locale = useLocale();

  const banner_image = images
    .filter((img) => img.format.name.includes("Banner"))
    .sort((a, b) => b.format.width - a.format.width)[0];

  const path = banner_image?.path?.processed;
  const uploadcare = !!path && isUploadcare(path);

  // The src the banner should actually display: reframed for Uploadcare,
  // untouched otherwise (legacy imgix).
  const displaySrc = uploadcare ? buildUploadcareSrc(path) : path;

  // Localisation left exactly as it was — just operating on displaySrc.
  const [src, setSrc] = useState(displaySrc);
  useEffect(() => {
    if (locale === "de" && displaySrc) {
      setSrc(
        displaySrc.replace(
          /\.(?!.*\.)/, // last occurance of "." in the string
          "_de."
        )
      );
    } else {
      setSrc(displaySrc);
    }
  }, [locale, displaySrc]);

  return path ? (
    <AspectRatio ratio={BANNER_RATIO}>
      <Image
        src={src}
        width={uploadcare ? TARGET_WIDTH : banner_image.format.width}
        height={uploadcare ? TARGET_HEIGHT : banner_image.format.height}
        alt={eventName}
        style={{
          borderRadius: "var(--radius-4)",
        }}
        className="w-full aspect-[3.28/1] object-cover"
        onError={() => setSrc(path)}
        quality={90}
      />
    </AspectRatio>
  ) : (
    <div className="flex items-center justify-center rounded-xl aspect-[6/7] max-h-[150px] md:max-h-[264px] bg-[color:var(--gray-3)]">
      <MegaphoneIcon className="text-[color:var(--gray-11)] w-1/2 h-1/2" />
    </div>
  );
}
